import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Layout/Header';
import Modal from '../components/UI/Modal';
import { Plus, Copy, Pencil } from 'lucide-react';

export default function Suppliers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null); // 'add' | 'edit'
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', contact_name: '', email: '', phone: '', category_ids: [], notes: '' });

  const load = () => api('/api/suppliers').then(r => setSuppliers(r.data)).catch(console.error);
  useEffect(() => { load(); api('/api/categories').then(r => setCategories(r.data)).catch(() => {}); }, []);

  const openAdd = () => {
    setForm({ name: '', contact_name: '', email: '', phone: '', category_ids: [], notes: '' });
    setEditId(null);
    setModal('add');
  };

  const openEdit = (s) => {
    setForm({
      name: s.name || '', contact_name: s.contact_name || '', email: s.email || '',
      phone: s.phone || '', category_ids: (s.categories || []).map(c => c.id), notes: s.notes || ''
    });
    setEditId(s.id);
    setModal('edit');
  };

  const toggleCat = (catId) => {
    setForm(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(catId)
        ? prev.category_ids.filter(id => id !== catId)
        : [...prev.category_ids, catId]
    }));
  };

  const submit = async () => {
    try {
      const body = { ...form };
      if (modal === 'edit') {
        await api('/api/suppliers/' + editId, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await api('/api/suppliers', { method: 'POST', body: JSON.stringify(body) });
      }
      setModal(null); load();
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(window.location.origin + '/portal/' + token);
    alert('Portal link copied!');
  };

  const canEdit = ['admin', 'manager'].includes(user?.role);

  return (
    <div>
      <Header title="Suppliers">
        {canEdit && <button className="btn btn-primary" onClick={openAdd}><Plus size={16} /> Add Supplier</button>}
      </Header>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Contact</th><th>Email</th><th>Phone</th><th>Categories</th><th>Portal</th>{canEdit && <th>Actions</th>}</tr></thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.id}>
                <td><span onClick={(e) => { e.stopPropagation(); nav('/orders?supplier_id=' + s.id); }} style={{ fontWeight: 600, cursor: 'pointer', borderBottom: '1px dashed var(--faint)', paddingBottom: 1 }} onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text)'}>{s.name}</span></td>
                <td>{s.contact_name}</td>
                <td>{s.email}</td>
                <td>{s.phone}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(s.categories || []).map(c => (
                      <span key={c.id} className="cat-badge" style={{ background: c.color + '20', color: c.color, fontSize: 11 }}>{c.name.split(' ')[0]}</span>
                    ))}
                    {(!s.categories || !s.categories.length) && <span style={{ color: 'var(--faint)' }}>-</span>}
                  </div>
                </td>
                <td>
                  {s.portal_token && (
                    <button className="btn btn-secondary btn-sm" onClick={() => copyToken(s.portal_token)}>
                      <Copy size={14} /> Copy
                    </button>
                  )}
                </td>
                {canEdit && (
                  <td>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}><Pencil size={14} /> Edit</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal === 'edit' ? 'Edit Supplier' : 'Add Supplier'} onClose={() => setModal(null)}>
          <div className="form-group"><label>Company Name</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
          <div className="grid-2">
            <div className="form-group"><label>Contact Name</label><input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
            <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
          </div>
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div className="form-group">
            <label>Categories Supplied</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {categories.map(c => (
                <label key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  background: form.category_ids.includes(c.id) ? c.color + '18' : 'var(--card)',
                  border: '1px solid ' + (form.category_ids.includes(c.id) ? c.color : 'var(--border)'),
                  borderRadius: 8, cursor: 'pointer', fontSize: 13, transition: 'all .15s',
                  color: form.category_ids.includes(c.id) ? c.color : 'var(--muted)'
                }}>
                  <input type="checkbox" checked={form.category_ids.includes(c.id)} onChange={() => toggleCat(c.id)}
                    style={{ width: 'auto', accentColor: c.color }} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>{modal === 'edit' ? 'Save Changes' : 'Add Supplier'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
